import { useAuth } from '../../context/AuthContext';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import MobileBackButton from '../../components/MobileBackButton';

export default function HelpCenterPage() {
  const { isAuthenticated, loading } = useAuth();
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedItems, setExpandedItems] = useState([]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      router.push({
        pathname: '/login',
        query: { redirect: router.asPath }
      });
    }
  }, [isAuthenticated, loading, router]);

  // Help categories for CreoleCentric
  const categories = [
    { id: 'all', name: 'All Categories', icon: '📚' },
    { id: 'getting-started', name: 'Getting Started', icon: '🚀' },
    { id: 'account', name: 'Account & Billing', icon: '💳' },
    { id: 'api', name: 'API & Integration', icon: '⚙️' },
    { id: 'voices', name: 'Voice Models', icon: '🎤' },
    { id: 'technical', name: 'Technical Issues', icon: '🔧' },
    { id: 'payment', name: 'Payment Methods', icon: '💰' },
    { id: 'learning', name: 'Learn Creole', icon: '📚' },
  ];

  // Real FAQ items for CreoleCentric
  const faqItems = [
    // Getting Started
    {
      question: "How do I create my first Haitian Creole text-to-speech?",
      answer: "After logging in, click on 'Text to Speech' in the sidebar. Enter or paste your text (in any of our 50+ supported languages), select a Haitian Creole voice from our library, adjust settings like speed and pitch if desired, then click 'Generate Audio'. Your audio will be ready in seconds!",
      category: "getting-started"
    },
    {
      question: "What languages can I translate from?",
      answer: "CreoleCentric supports automatic translation from over 50 languages including French, English, Spanish, Portuguese, Chinese, Arabic, Hindi, and many more. Simply paste your text in any supported language, and our AI will automatically detect and translate it to Haitian Creole before generating speech.",
      category: "getting-started"
    },
    {
      question: "How do I download my generated audio files?",
      answer: "After generating audio, you'll see a player with download options. Click the download button and choose your preferred format: MP3 (recommended for most uses), WAV (for professional audio editing), or WebM (for web applications). Files are also saved in your history for 30 days.",
      category: "getting-started"
    },
    {
      question: "How can I use CreoleCentric as a mobile app?",
      answer: "CreoleCentric works as a Progressive Web App (PWA)! On iPhone/iPad: Open the site in Safari, tap the Share button, select 'Add to Home Screen', and tap 'Add'. On Android: Open the site in Chrome, tap the menu (three dots), select 'Add to Home Screen' or 'Install App', and confirm. The app will run fullscreen without browser controls, giving you maximum screen space and a native app experience!",
      category: "getting-started"
    },
    
    // Account & Billing
    {
      question: "How are credits calculated?",
      answer: "Credits are based on character count: 1 credit = 1 character. This includes spaces and punctuation. For example, a 1,000 character text costs 1,000 credits. Subscription plans provide monthly credits that refresh each billing cycle, while purchased credits never expire.",
      category: "account"
    },
    {
      question: "What's the difference between subscription and purchased credits?",
      answer: "Subscription credits are allocated monthly based on your plan and expire at the end of each billing period (except for plans with rollover). Purchased credits never expire and serve as a backup when subscription credits run out. We always use subscription credits first.",
      category: "account"
    },
    {
      question: "Which plans include credit rollover?",
      answer: "Creator, Pro, Business, and Enterprise plans include automatic credit rollover. Unused credits accumulate up to 3x your monthly allocation. For example, on the Creator plan (100,000 credits/month), you can accumulate up to 300,000 credits total. The Free and Learner plans do not include rollover.",
      category: "account"
    },
    {
      question: "How do I cancel or downgrade my subscription?",
      answer: "Go to the Subscription page and select a different plan or click 'Cancel Subscription'. Downgrades are scheduled for your next billing date, so you keep current benefits until then. Cancellations take effect at the end of your billing period. Warning: Downgrading to a plan without rollover may result in credit loss.",
      category: "account"
    },
    {
      question: "Where can I view or print a receipt for my subscription?",
      answer: "Open My Account → Credits → Billing history (or click 'Billing history' from the Credits page under Actions). Every subscription payment on your account is listed. Click 'View receipt' on a row to open a printable receipt with your name, the plan, billing period, amount paid, and a reference number. Use the Print / Save as PDF button — the saved file is named CCL_Receipt_MMDDYY-MMDDYY so a folder of receipts sorts chronologically. For iOS in-app purchases, the row links out to apps.apple.com/account/billing (Apple owns those receipts).",
      category: "payment"
    },
    {
      question: "I don't see a failed payment on my Billing history — where is it?",
      answer: "Failed payments don't appear in Billing history. A declined charge doesn't create a payment record on your account, so there's nothing to receipt. If you were expecting a receipt for a specific charge that isn't listed, please contact support. Refunds are separate entries in your Credits transaction history (not on the Billing history page).",
      category: "payment"
    },
    
    // Payment Methods
    {
      question: "What payment methods do you accept?",
      answer: "We accept: 1) Credit/Debit cards (Visa, Mastercard, Amex) via Stripe, 2) PayPal for both subscriptions and one-time purchases, 3) Cryptocurrency (Bitcoin, Ethereum, USDT, USDC) via NOWPayments. All payment methods support both subscription plans and credit package purchases.",
      category: "payment"
    },
    {
      question: "How do cryptocurrency payments work?",
      answer: "Select 'Cryptocurrency' as your payment method, choose your preferred crypto (we recommend USDT/USDC stablecoins to avoid price volatility), and you'll receive a payment address and QR code. Send the exact amount shown within 30 minutes. Your credits are added automatically once the payment confirms on the blockchain.",
      category: "payment"
    },
    {
      question: "Are cryptocurrency subscriptions automatic?",
      answer: "No, crypto subscriptions require manual renewal each billing period. You'll receive email reminders before your subscription expires. We're working on automatic crypto subscriptions for future release. For now, we recommend using Stripe or PayPal for automatic recurring payments.",
      category: "payment"
    },
    
    // Voice Models
    {
      question: "What Haitian Creole voices are available?",
      answer: "We offer 15+ high-quality Haitian Creole voices with various characteristics: different ages (child, young adult, adult, elder), genders (male, female), and regional accents from Port-au-Prince, Cap-Haïtien, Gonaïves, and more. Each voice can be customized for speed, pitch, and emphasis.",
      category: "voices"
    },
    {
      question: "Can I create a custom voice from my own audio?",
      answer: "Custom Voices is currently in beta for Enterprise customers. You'll need 30 minutes of clear audio recordings of the target voice. The custom voice will be available exclusively in your account. Contact support@creolecentric.com to join the beta program.",
      category: "voices"
    },
    {
      question: "How do I create custom pronunciations?",
      answer: "Navigate to 'Pronunciation Dictionary' in your account. Add entries for words that need special pronunciation by providing the word and its phonetic spelling. This is especially useful for names, technical terms, or regional expressions. Dictionaries can be applied to any voice model.",
      category: "voices"
    },
    {
      question: "What is Streaming Processing Mode and how do I use it?",
      answer: "Streaming is one of two Processing Modes (Standard / Streaming) — pick it in the picker above the Generate Speech button. Audio starts playing within about a second of clicking Generate, instead of waiting for the whole clip to render. Streaming is available whenever you've selected a Creole Regional or Creole Studio Pro voice. Inputs of any length are accepted: long text is automatically split at sentence boundaries into ≤600-character phrases and streamed sequentially through one continuous audio player — the brief pause between phrases lands at a sentence break where a natural pause was already expected. For inputs over 600 characters you'll see a confirmation modal showing the phrase count and total credit cost before submission, so there are no surprises. A Stop streaming button lets you abort mid-playback; the in-progress phrase is cancelled and its credits are refunded in full, while phrases that already finished and were added to your History are kept and billed normally. Streaming is billed at 1.5x credits. Custom Voices don't support streaming — they need the full audio to apply your cloned voice — so the Streaming button is disabled when a Custom Voice is selected.",
      category: "voices"
    },
    
    // API & Integration
    {
      question: "How do I get started with the API?",
      answer: "First, ensure you have API access through either a Creator+ subscription plan OR by purchasing the Creator Pack (85,000+ credits). Then go to 'API Keys' in your account to generate your API key. Our REST API uses standard HTTP requests with JSON. Base URL: https://creolecentric.com/v1/. Include your API key in the Authorization header: 'Bearer YOUR_API_KEY'. Check our API documentation for endpoints and examples.",
      category: "api"
    },
    {
      question: "Can I get API access without a subscription?",
      answer: "Yes! You can access our API by purchasing 85,000 or more credits (the Creator Pack). Credit-based API access tracks the subscription tiers: 85,000–424,999 credits (Creator level: 5 keys, Regional 300/hr + Studio 1,000/hr), 425,000–1,699,999 (Pro level: 10 keys, Regional 1,000/hr + Studio 3,000/hr), 1,700,000–8,999,999 (Business level: 25 keys, Regional 3,000/hr + Studio 5,000/hr), 9,000,000+ (Enterprise level: 25 keys, Regional custom + Studio 20,000/hr custom). Balances below 85,000 credits (e.g., the Learner Pack at 25,000) do not include API access. Your tier level adjusts dynamically as you consume credits.",
      category: "api"
    },
    {
      question: "What are the API rate limits?",
      answer: "Hourly request limits are per account and vary by engine (Creole Regional vs. Creole Studio / Studio Pro). Free/Learner have no API access. Creator: Regional 300/hr, Studio 1,000/hr. Pro: Regional 1,000/hr, Studio 3,000/hr. Business: Regional 3,000/hr, Studio 5,000/hr. Enterprise: Regional custom, Studio 20,000/hr (custom). The same limits apply whether you have a subscription or credit-based access. Rate limit info is included in response headers: X-RateLimit-Limit and X-RateLimit-Remaining.",
      category: "api"
    },
    {
      question: "Do you provide SDK libraries?",
      answer: "Yes! We offer official SDKs for Python, JavaScript/Node.js, PHP, and Java. Find them on our GitHub page or install via package managers: pip install creolecentric (Python), npm install @creolecentric/sdk (JavaScript). SDKs handle authentication, rate limiting, and provide convenient methods for all API operations.",
      category: "api"
    },
    {
      question: "Can I use webhooks for async processing?",
      answer: "Yes, for batch operations and long-running tasks, provide a webhook_url in your API request. We'll POST to your URL when processing completes, including the job_id and download links. Webhooks are recommended for batches over 10,000 characters to avoid timeouts.",
      category: "api"
    },
    
    // Technical Issues
    {
      question: "Why is my audio generation taking so long?",
      answer: "Generation typically takes 1-3 seconds per 1,000 characters. Longer delays may be due to: 1) High system load (check our /status page), 2) Very long texts (consider breaking into smaller chunks), 3) Network issues. If generation takes over 60 seconds, try refreshing and regenerating.",
      category: "technical"
    },
    {
      question: "What's the maximum text length I can process?",
      answer: "Single requests are limited by plan: Free (1,000 chars), Learner (3,000 chars), Creator (5,000 chars), Pro/Business (7,500 chars), Enterprise (10,000 chars). For longer texts, use our batch processing API or split the text into multiple requests.",
      category: "technical"
    },
    {
      question: "Why does my text highlighting not sync with audio?",
      answer: "Highlighting sync issues can occur with: 1) Texts containing many numbers or abbreviations, 2) Mixed language content, 3) Heavy browser load. Try: refreshing the page, using a different browser, or disabling browser extensions. Contact support if issues persist.",
      category: "technical"
    },
    {
      question: "Audio files won't play or download - what should I do?",
      answer: "First, check your browser allows pop-ups and downloads from creolecentric.com. Clear your browser cache and cookies. Ensure you have sufficient storage space. Try a different browser or device. If using Safari, check that 'Prevent cross-site tracking' is disabled for our site.",
      category: "technical"
    },

    // Learn Creole
    {
      question: "What is the Learn Creole module?",
      answer: "Learn Creole is our interactive language learning module with 27+ lesson categories, multiple study methods (flashcards, quizzes, fill-in-the-blank, sentence builder, dialogues, grammar lessons), an alphabet drill, and a Creole Calculator. Progress from Beginner to Expert at your own pace. See our full Learn Creole documentation for details.",
      category: "learning"
    },
    {
      question: "How does the Creole Calculator work?",
      answer: "The Creole Calculator lets you enter any number from 0 to 999 trillion and see the full Haitian Creole numeral word. For example, entering '42' shows 'karant de'. It includes audio pronunciation so you can hear how each number is spoken in Creole. Access it from the Learn Creole module.",
      category: "learning"
    },
    {
      question: "What is Smart Format?",
      answer: "Smart Format automatically processes your text so numbers, dates, acronyms, and special content are spoken naturally. For example, '2024' is read as a full number rather than individual digits, and acronyms like 'UN' are spelled out. It's enabled by default and can be toggled in Settings > Text Processing.",
      category: "technical"
    },
    {
      question: "What does Creolize French Words do?",
      answer: "Creolize French Words detects French words in your text and suggests Haitian Creole equivalents for more authentic pronunciation. When enabled, a review modal appears before generation showing detected French words with their Creole alternatives. You can accept, reject, or modify each suggestion. Toggle it in Settings > Text Processing.",
      category: "technical"
    },
    {
      question: "How do I get the best results with Haitian Creole audio transcription?",
      answer: "Haitian Creole audio transcription is now supported for microphone input and audio/video files. For best results, explicitly select 'Haitian Creole' as the source language rather than using auto-detect, which may occasionally misidentify Creole as French. Clear audio with minimal background noise will produce the most accurate transcriptions.",
      category: "technical"
    },
    {
      question: "What's the difference between Standard and Streaming mode?",
      answer: "Standard mode processes audio asynchronously (queued) at 1x credit cost with your full plan character limit. Streaming mode starts playback within about a second and streams the rest of the audio as it's generated; billed at 1.5x credits and supported by Creole Regional and Creole Studio Pro. Long inputs are auto-split into ≤600-char phrases that stream sequentially. Streaming mode is available on Learner+ plans only.",
      category: "technical"
    },
    {
      question: "How do I add my own words to the dictionary?",
      answer: "Open the Dictionary page and click the 'Add Word' button. Fill in the Creole word, English definition, part of speech, and optionally add usage examples and IPA pronunciation. Your entry will appear alongside standard entries marked with a 'My Entry' badge. See our Dictionary documentation for more details.",
      category: "learning"
    }
  ];

  // Filter FAQs based on active category and search query
  const filteredFAQs = faqItems.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleExpanded = (index) => {
    setExpandedItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  if (!isAuthenticated) {
    return null; // Don't render anything while redirecting
  }
  
  return (
    <div className="flex-1 p-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <MobileBackButton />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Help Center</h1>
        </div>
        
        {/* Search Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 rounded-lg p-8 mb-8 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">How can we help you?</h2>
            <p className="text-blue-100 mb-6">Search our knowledge base or browse categories below</p>
            <div className="relative">
              <input
                type="text"
                placeholder="Search for answers..."
                className="w-full px-5 py-3 pr-12 rounded-full text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-blue-400 dark:focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {/* AI Assistant Prompt */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Need instant help? Try our AI Assistant!
              </h3>
              <p className="text-blue-800 dark:text-blue-200 mb-4">
                Our AI Assistant can answer most questions instantly and help with common issues. It's available 24/7 and can guide you through using CreoleCentric's features.
              </p>
              <Link href="/contact" className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                Chat with AI Assistant
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Link href="/docs/documentation" className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">📖</div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Documentation</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Read the docs</p>
              </div>
            </div>
          </Link>
          <Link href="/api-keys" className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">🔑</div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">API Reference</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Developer guide</p>
              </div>
            </div>
          </Link>
          <a href="mailto:support@creolecentric.com" className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">✉️</div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Email Support</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Get help</p>
              </div>
            </div>
          </a>
          <Link href="/status" className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">🟢</div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">System Status</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Check uptime</p>
              </div>
            </div>
          </Link>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Categories Sidebar */}
          <div className="lg:w-1/4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">Categories</h3>
              </div>
              <div className="p-4">
                <nav className="space-y-1">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      className={`w-full text-left px-4 py-2.5 rounded-md text-sm flex items-center space-x-2 transition-colors ${
                        activeCategory === category.id 
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setActiveCategory(category.id)}
                    >
                      <span>{category.icon}</span>
                      <span>{category.name}</span>
                    </button>
                  ))}
                </nav>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Need more help?</h4>
                <Link href="/contact" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center group mb-2">
                  Chat with AI Assistant
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </Link>
                <a href="mailto:support@creolecentric.com" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center group">
                  Email Support
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          
          {/* FAQ Content */}
          <div className="lg:w-3/4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {activeCategory === 'all' ? 'Frequently Asked Questions' : 
                    `${categories.find(c => c.id === activeCategory)?.name} FAQs`}
                </h3>
                {filteredFAQs.length === 0 && searchQuery && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    No results found for "{searchQuery}"
                  </p>
                )}
              </div>
              
              {filteredFAQs.length > 0 ? (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredFAQs.map((faq, index) => (
                    <div key={index} className="group">
                      <button
                        onClick={() => toggleExpanded(index)}
                        className="w-full flex justify-between items-center px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <span className="font-medium text-gray-900 dark:text-white pr-8">{faq.question}</span>
                        <span className={`text-blue-500 dark:text-blue-400 flex-shrink-0 transition-transform ${expandedItems.includes(index) ? 'rotate-180' : ''}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </span>
                      </button>
                      {expandedItems.includes(index) && (
                        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/30 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <div className="inline-block p-4 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No results found</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    {searchQuery ? `We couldn't find any results for "${searchQuery}"` : "No FAQs available in this category yet."}
                  </p>
                  <button
                    className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory('all');
                    }}
                  >
                    View all FAQs
                  </button>
                </div>
              )}
            </div>
            
            {/* Additional Resources */}
            <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Additional Resources</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Video Tutorials</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Learn how to use CreoleCentric with our step-by-step video guides.
                  </p>
                  <a href="https://youtube.com/@creolecentric" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline text-sm">
                    Watch tutorials →
                  </a>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Community Forum</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Connect with other users, share tips, and get community support.
                  </p>
                  <a href="https://community.creolecentric.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline text-sm">
                    Join community →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}